import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { styled } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Icon } from '@iconify/react'
import moment from 'moment'
import { Button } from '../../../../../components/buttons'
import BigFireIcon from '../../../../../components/icons/BigFireIcon'
import { colorPalette } from '../../../../../style/colorPalette'
import { intl } from '../../../../../../i18n'
import { DISPLAY_DATE_FORMAT } from '../../../../../../utils/date'
import {
  DEFAULT_MILESTONE_COLOR,
  DEFAULT_MILESTONE_ICON,
  getMilestoneStatus,
  MilestoneDetail,
} from '../../../../../../domain/entity/MilestoneEntity'
import { useMilestoneRepository } from '../../../../../../services/adaptors/milestoneRepositoryAdaptor'
import {
  addGlobalMessage,
  MessageLevel,
} from '../../../../../../store/messages'
import SingleMilestoneDialog from '../../../../ProjectPlanNew/components/Header/SingleMilestoneDialog'

type MilestoneTabProps = {
  uuid: string
}

export const MilestoneTab = ({ uuid }: MilestoneTabProps) => {
  const dispatch = useDispatch()
  const milestoneRepository = useMilestoneRepository()
  const [milestones, setMilestones] = useState<MilestoneDetail[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingMilestone, setEditingMilestone] = useState<
    MilestoneDetail | undefined
  >()

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const all = await milestoneRepository.fetchAll()
      setMilestones(
        all
          .filter(m => m.wbsItemUuid === uuid)
          .sort((a, b) => a.milestoneDate.localeCompare(b.milestoneDate))
      )
    } catch (error) {
      console.error('Failed to fetch milestones:', error)
      dispatch(
        addGlobalMessage({
          type: MessageLevel.ERROR,
          title: intl.formatMessage({ id: 'milestone.error.fetchFailed' }),
        })
      )
    } finally {
      setIsLoading(false)
    }
  }, [milestoneRepository, uuid, dispatch])

  useEffect(() => {
    load()
  }, [load])

  const handleAdd = useCallback(() => {
    setEditingMilestone(undefined)
    setDialogOpen(true)
  }, [])

  const handleEdit = useCallback((milestone: MilestoneDetail) => {
    setEditingMilestone(milestone)
    setDialogOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setDialogOpen(false)
  }, [])

  return (
    <Container>
      <Header>
        <Button colorPattern="skyBlue" variant="outlined" onClick={handleAdd}>
          <AddIcon sx={{ fontSize: 16 }} />
          {intl.formatMessage({ id: 'milestone.popover.add' })}
        </Button>
      </Header>

      {!isLoading && milestones.length === 0 && (
        <EmptyContainer>
          {intl.formatMessage({ id: 'milestone.empty' })}
        </EmptyContainer>
      )}

      <List>
        {milestones.map(milestone => (
          <MilestoneRow
            key={milestone.uuid}
            onClick={() => handleEdit(milestone)}
          >
            <MilestoneStatusIcon milestone={milestone} />
            <RowIcon iconColor={milestone.color ?? DEFAULT_MILESTONE_COLOR}>
              <Icon
                icon={milestone.iconName ?? DEFAULT_MILESTONE_ICON}
                width={18}
                height={18}
                color={milestone.color ?? DEFAULT_MILESTONE_COLOR}
              />
            </RowIcon>
            <RowName>{milestone.name}</RowName>
            <RowDate>
              {moment(milestone.milestoneDate).format(DISPLAY_DATE_FORMAT)}
            </RowDate>
          </MilestoneRow>
        ))}
      </List>

      <SingleMilestoneDialog
        open={dialogOpen}
        onClose={handleClose}
        onSaveComplete={load}
        milestone={editingMilestone}
        defaultWbsItemUuid={uuid}
      />
    </Container>
  )
}

const MilestoneStatusIcon = ({ milestone }: { milestone: MilestoneDetail }) => {
  const status = getMilestoneStatus(milestone)
  if (status === 'achieved') return <StyledCheckCircleIcon />
  if (status === 'overdue') return <BigFireIcon style={{ fontSize: 18 }} />
  return <StyledCheckCircleOutlineIcon />
}

const Container = styled('div')({
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
})

const Header = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
})

const EmptyContainer = styled('div')({
  padding: 24,
  textAlign: 'center',
  color: colorPalette.monotone[4],
  fontSize: 13,
})

const List = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const MilestoneRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 8px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const RowIcon = styled('div')<{ iconColor: string }>(({ iconColor }) => ({
  display: 'flex',
  color: iconColor,
  flexShrink: 0,
}))

const RowName = styled('div')({
  flex: 1,
  minWidth: 0,
  fontSize: 13,
  color: colorPalette.monotone[9],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const RowDate = styled('div')({
  fontSize: 12,
  color: colorPalette.monotone[5],
  flexShrink: 0,
})

const StyledCheckCircleIcon = styled(CheckCircleIcon)({
  fontSize: 18,
  flexShrink: 0,
  color: colorPalette.green[5],
})

const StyledCheckCircleOutlineIcon = styled(CheckCircleOutlineIcon)({
  fontSize: 18,
  flexShrink: 0,
  color: colorPalette.monotone[4],
})
